import { useCallback, useContext, useEffect, useState } from 'react';
import { Address as AddressType } from 'shared/types/account';
import Link from 'components/gruenland/atoms/link';
import Typography from 'components/gruenland/atoms/typography';
import { AccountContext } from 'context/account';
import toAccountUrl from 'helpers/account/toAccountUrl';
import { useFormat } from 'helpers/hooks/useFormat';
import { TYPOGRAPHY_PRIVATE_AREA_TITLE } from 'lib/typography';
import Address from './address';
import { AddressFormData } from './address-form';
import usePropsToAddressType from './mapPropsToAddressType';

const Addresses = () => {
  const { formatMessage } = useFormat({ name: 'gruenland' });

  const { mapPropsToAddress } = usePropsToAddressType();

  const { account, defaultBillingAddress, defaultShippingAddress } = useContext(AccountContext);
  const addresses = account?.addresses;

  const [selectedBillingAddress, setSelectedBillingAddress] = useState<AddressType>();
  const [selectedShippingAddress, setSelectedShippingAddress] = useState<AddressType>();

  const selectAddress = (address: AddressFormData) => {
    address.isBillingAddress ? setSelectedBillingAddress(address) : setSelectedShippingAddress(address);
  };

  const setAddressAsDefault = useCallback(
    (address: AddressType) => {
      const { setAsDefault } = mapPropsToAddress(address as AddressFormData);
      setAsDefault();
    },
    [mapPropsToAddress],
  );

  useEffect(() => {
    if (selectedBillingAddress && selectedBillingAddress !== defaultBillingAddress?.addressId) {
      setAddressAsDefault(selectedBillingAddress as AddressType);
    }
  }, [defaultBillingAddress?.addressId, selectedBillingAddress, setAddressAsDefault]);

  useEffect(() => {
    if (selectedShippingAddress && selectedShippingAddress !== defaultShippingAddress?.addressId) {
      setAddressAsDefault(selectedShippingAddress as AddressType);
    }
  }, [defaultShippingAddress?.addressId, selectedShippingAddress, setAddressAsDefault]);

  useEffect(() => {
    if (!selectedBillingAddress) {
      setSelectedBillingAddress(defaultBillingAddress);
    }

    if (!selectedShippingAddress) {
      setSelectedShippingAddress(defaultShippingAddress);
    }
  }, [defaultBillingAddress, defaultShippingAddress, selectedBillingAddress, selectedShippingAddress]);

  return (
    <>
      <Typography as="h2" className={TYPOGRAPHY_PRIVATE_AREA_TITLE}>
        {formatMessage({
          id: 'privatearea.addresses',
          defaultMessage: 'Indirizzi',
        })}
      </Typography>
      <Typography>
        {formatMessage({
          id: 'privatearea.addressesDesc',
          defaultMessage: 'Gestisci i tuoi indirizzi di spedizione e fatturazione.',
        })}
      </Typography>
      <Link variant="buttonPrimaryGreen" link={toAccountUrl('?hash=addresses&id=address-add')}>
        {formatMessage({ id: 'address.add', defaultMessage: 'Aggiungi indirizzo' })}
      </Link>

      <form className="mt-6 grid gap-5 pb-7 md:mt-7 2xl:mt-8">
        {addresses?.map((address) => (
          <Address
            key={address.addressId}
            address={address}
            isChecked={false}
            selectAddress={selectAddress}
            isDefaultAddress={
              address.addressId == selectedBillingAddress?.addressId ||
              address.addressId == selectedShippingAddress?.addressId
            }
          />
        ))}
      </form>
    </>
  );
};

export default Addresses;
